import { useState, useEffect, useCallback } from "react";

export interface CartItem {
  id: number;
  name: string;
  nameF: string;
  price: number;
  image: string;
  quantity: number;
}

const CART_KEY = "creative_hub_cart";

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  localStorage.setItem(CART_KEY, JSON.stringify(items));
}

let listeners: Array<() => void> = [];

function notifyAll() {
  listeners.forEach((fn) => fn());
}

export function useCart() {
  const [items, setItems] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    const handler = () => setItems(loadCart());
    listeners.push(handler);
    return () => {
      listeners = listeners.filter((l) => l !== handler);
    };
  }, []);

  const addToCart = useCallback((product: Omit<CartItem, "quantity">) => {
    const current = loadCart();
    const existing = current.find((i) => i.id === product.id);
    let updated: CartItem[];
    if (existing) {
      updated = current.map((i) =>
        i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
      );
    } else {
      updated = [...current, { ...product, quantity: 1 }];
    }
    saveCart(updated);
    setItems(updated);
    notifyAll();
  }, []);

  const removeFromCart = useCallback((id: number) => {
    const updated = loadCart().filter((i) => i.id !== id);
    saveCart(updated);
    setItems(updated);
    notifyAll();
  }, []);

  const updateQuantity = useCallback((id: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }
    const updated = loadCart().map((i) =>
      i.id === id ? { ...i, quantity } : i
    );
    saveCart(updated);
    setItems(updated);
    notifyAll();
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    saveCart([]);
    setItems([]);
    notifyAll();
  }, []);

  const cartCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const cartTotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return { items, addToCart, removeFromCart, updateQuantity, clearCart, cartCount, cartTotal };
}